import { VideoMetadata, InitiateUploadResponse, CompleteUploadResponse, Video, UploadProgress } from './types';
import config from '../config';

export class VideoApi {
  private baseUrl: string;
  private uploadProgress: Map<string, { progress: number; status: 'uploading' | 'completed' | 'failed'; error?: string }>;

  constructor() {
    this.baseUrl = config.apiUrl;
    this.uploadProgress = new Map();
  }

  async initiateUpload(metadata: VideoMetadata, token: string, passcode?: string): Promise<{ videoId: string; uploadUrl: string }> {
    const requestBody = {
      ...metadata,
      content_type: metadata.content_type || 'video/mp4',
      passcode: passcode
    };
    
    const response = await fetch(`${this.baseUrl}/upload/initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to initiate upload');
    }

    const data = await response.json();
    return {
      videoId: data.video_id,
      uploadUrl: data.upload_url
    };
  }

  async uploadVideo(file: File, metadata: VideoMetadata, token: string, processingOptions?: {
    generate_transcript?: boolean;
    detect_speakers?: boolean;
    extract_objects?: boolean;
  }, passcode?: string): Promise<Video> {
    try {
      // Validate file size with passcode support
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      const maxSizeWithPasscode = 500 * 1024 * 1024; // 500MB in bytes
      const hasValidPasscode = passcode === 'BETA2025';
      
      if (file.size > maxSize && !hasValidPasscode) {
        if (file.size <= maxSizeWithPasscode) {
          throw new Error(`File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds the 100MB limit. Enter the beta passcode to upload files up to 500MB.`);
        } else {
          throw new Error(`File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds the maximum allowed size of 500MB.`);
        }
      }
      
      if (file.size > maxSizeWithPasscode) {
        throw new Error(`File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds the maximum allowed size of 500MB.`);
      }
      
      // Step 1: Initiate upload
      const { videoId, uploadUrl } = await this.initiateUpload(metadata, token, passcode);
      
      if (!uploadUrl) {
        throw new Error('No upload URL received from server');
      }
      
      // Initialize progress tracking
      this.uploadProgress.set(videoId, { progress: 0, status: 'uploading' });
      
      // Step 2: Upload to GCS
      console.log('Starting real upload process...');
      console.log('File size:', file.size);
      console.log('File type:', file.type);
      console.log('Upload URL:', uploadUrl);
      console.log('File name:', file.name);
      console.log('File last modified:', file.lastModified);
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          'x-goog-content-length-range': hasValidPasscode ? '0,524288000' : '0,104857600', // 500MB with passcode, 100MB without
        },
      });

      console.log('Upload response status:', uploadResponse.status);
      console.log('Upload response status text:', uploadResponse.statusText);
      console.log('Upload response headers:', Object.fromEntries(uploadResponse.headers.entries()));

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('Upload failed with status:', uploadResponse.status);
        console.error('Upload error response:', errorText);
        console.error('Response headers:', Object.fromEntries(uploadResponse.headers.entries()));
        throw new Error(`Failed to upload to cloud storage: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      console.log('Upload successful, response status:', uploadResponse.status);
      
      // Update progress to 100%
      this.uploadProgress.set(videoId, { progress: 100, status: 'uploading' });

      // Step 3: Complete upload with processing options
      const completeResponse = await fetch(`${this.baseUrl}/upload/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          video_id: videoId,
          metadata: metadata,
          status: "COMPLETED",
          processing_options: processingOptions || {
            generate_transcript: true,
            detect_speakers: true,
            extract_objects: true
          }
        }),
        credentials: 'include'
      });

      if (!completeResponse.ok) {
        const errorText = await completeResponse.text();
        console.error('Complete upload failed:', completeResponse.status, errorText);
        throw new Error('Failed to complete upload');
      }

      const completeData = await completeResponse.json();
      console.log('Complete upload response:', completeData);

      // Mark upload as completed
      this.uploadProgress.set(videoId, { progress: 100, status: 'completed' });

      // Return video details
      return {
        id: videoId,
        user_id: '', // Will be set by backend
        title: metadata.title,
        description: metadata.description,
        file_name: metadata.file_name,
        file_size: metadata.file_size,
        status: 'COMPLETED',
        gcs_path: '',
        playback_url: '',
        processing_progress: 0,
        processing_attempts: 0,
        transcript_available: 'false',
        speakers_detected: 'false',
        objects_detected: 'false',
        search_indexed: 'false',
        frame_count: 0,
        segment_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Upload error:', error);
      // Mark upload as failed
      const videoId = this.uploadProgress.keys().next().value;
      if (videoId) {
        this.uploadProgress.set(videoId, {
          progress: 0,
          status: 'failed',
          error: error instanceof Error ? error.message : 'Upload failed'
        });
      }
      throw error;
    }
  }

  async processVideo(videoId: string, processingOptions: {
    generate_transcript?: boolean;
    detect_speakers?: boolean;
    extract_objects?: boolean;
  }, token: string): Promise<{ message: string; video_id: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/videos/${videoId}/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: "COMPLETED",
          processing_options: processingOptions
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Process video failed:', response.status, errorText);
        throw new Error('Failed to process video');
      }

      const data = await response.json();
      console.log('Process video response:', data);
      return data;
    } catch (error) {
      console.error('Process video error:', error);
      throw error;
    }
  }

  async getVideo(id: string, token: string): Promise<Video | null> {
    try {
      const response = await fetch(`${this.baseUrl}/videos/${id}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error('Failed to fetch video details');
      }

      const data = await response.json();
      return {
        id: data.id,
        user_id: data.user_id || '',
        title: data.title,
        description: data.description,
        file_name: data.file_name,
        file_size: data.file_size,
        status: data.status,
        gcs_path: data.gcs_path,
        playback_url: data.playback_url,
        processing_progress: data.processing_progress || 0,
        processing_attempts: data.processing_attempts || 0,
        transcript_available: data.transcript_available || 'false',
        speakers_detected: data.speakers_detected || 'false',
        objects_detected: data.objects_detected || 'false',
        search_indexed: data.search_indexed || 'false',
        frame_count: data.frame_count || 0,
        segment_count: data.segment_count || 0,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };
    } catch (error) {
      console.error('Error fetching video:', error);
      return null;
    }
  }

  async listVideos(token: string, page: number = 1, pageSize: number = 10): Promise<Video[]> {
    try {
      const url = `${this.baseUrl}/videos/?page=${page}&page_size=${pageSize}`;
      console.log('Making request to:', url);
      console.log('Using baseUrl:', this.baseUrl);
      console.log('Full URL being requested:', url);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        console.error('Response not OK:', response.status, response.statusText);
        throw new Error('Failed to fetch videos');
      }

      const data = await response.json();
      return data.map((video: {
        id: string;
        user_id?: string;
        title: string;
        description?: string;
        file_name: string;
        file_size: number;
        status: string;
        gcs_path: string;
        playback_url?: string;
        processing_progress?: number;
        processing_attempts?: number;
        transcript_available?: string;
        speakers_detected?: string;
        objects_detected?: string;
        search_indexed?: string;
        frame_count?: number;
        segment_count?: number;
        created_at: string;
        updated_at: string;
      }) => ({
        id: video.id,
        user_id: video.user_id || '',
        title: video.title,
        description: video.description,
        file_name: video.file_name,
        file_size: video.file_size,
        status: video.status,
        gcs_path: video.gcs_path,
        playback_url: video.playback_url,
        processing_progress: video.processing_progress || 0,
        processing_attempts: video.processing_attempts || 0,
        transcript_available: video.transcript_available || 'false',
        speakers_detected: video.speakers_detected || 'false',
        objects_detected: video.objects_detected || 'false',
        search_indexed: video.search_indexed || 'false',
        frame_count: video.frame_count || 0,
        segment_count: video.segment_count || 0,
        created_at: video.created_at,
        updated_at: video.updated_at,
      }));
    } catch (error) {
      console.error('Error listing videos:', error);
      throw error;
    }
  }

  async getVideoStatus(videoId: string, token: string): Promise<{
    video_id: string;
    status: string;
    processing_progress: number;
    processing_started_at: string | null;
    processing_completed_at: string | null;
    processing_error: string | null;
    processing_attempts: number;
    processing_options: Record<string, unknown>;
    transcript_available: string;
    speakers_detected: string;
    objects_detected: string;
    created_at: string;
    updated_at: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/videos/${videoId}/status`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Get video status failed:', response.status, errorText);
        throw new Error('Failed to get video status');
      }

      const data = await response.json();
      console.log('Video status response:', data);
      return data;
    } catch (error) {
      console.error('Get video status error:', error);
      throw error;
    }
  }

  async retryVideoProcessing(videoId: string, token: string): Promise<{
    message: string;
    video_id: string;
    status: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/videos/${videoId}/retry-processing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Retry video processing failed:', response.status, errorText);
        throw new Error('Failed to retry video processing');
      }

      const data = await response.json();
      console.log('Retry video processing response:', data);
      return data;
    } catch (error) {
      console.error('Retry video processing error:', error);
      throw error;
    }
  }

  async indexVideoForSearch(videoId: string, token: string): Promise<{
    message: string;
    video_id: string;
    status: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/videos/${videoId}/index-for-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Search indexing failed:', response.status, errorText);
        throw new Error('Failed to start search indexing');
      }

      const data = await response.json();
      console.log('Search indexing response:', data);
      return data;
    } catch (error) {
      console.error('Search indexing error:', error);
      throw error;
    }
  }

  getUploadProgress(videoId: string) {
    return this.uploadProgress.get(videoId);
  }
} 